import { DatePicker, DatePickerFieldTrigger } from "../date-picker/date-picker.tsx";
import { Select } from "../select/select.tsx";
import styles from "./date-time-picker.module.css";

type Meridiem = "AM" | "PM";

export type DateTimePickerProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  minDate?: Date;
  disabled?: boolean;
  required?: boolean;
};

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, index) => {
  const value = String(index + 1);
  return { value, label: value.padStart(2, "0") };
});

const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, index) => {
  const value = String(index * 5).padStart(2, "0");
  return { value, label: value };
});

const MERIDIEM_OPTIONS = [{ value: "AM", label: "AM" }, { value: "PM", label: "PM" }];

function parts(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return { date: null, hour: "12", minute: "00", meridiem: "PM" as Meridiem };
  const hour24 = Number(match[4]);
  return {
    date: new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
    hour: String(hour24 % 12 || 12),
    minute: String(Math.floor(Number(match[5]) / 5) * 5).padStart(2, "0"),
    meridiem: (hour24 >= 12 ? "PM" : "AM") as Meridiem,
  };
}

function format(date: Date, hour: string, minute: string, meridiem: Meridiem): string {
  const hour12 = Number(hour);
  const hour24 = meridiem === "AM" ? hour12 % 12 : (hour12 % 12) + 12;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}T${String(hour24).padStart(2, "0")}:${minute}`;
}

export function DateTimePicker({ id, label, value, onChange, minDate, disabled = false, required = false }: DateTimePickerProps) {
  const current = parts(value);
  const updateTime = (hour: string, minute: string, meridiem: Meridiem) => {
    if (current.date) onChange(format(current.date, hour, minute, meridiem));
  };

  return <fieldset className={styles.fieldset} disabled={disabled}>
    <legend>{label}{required ? <span aria-hidden="true"> *</span> : null}</legend>
    <div className={styles.fields}>
      <DatePicker
        id={`${id}-date`}
        selected={current.date}
        minDate={minDate}
        disabled={disabled}
        required={required}
        placeholderText="Select date"
        customInput={<DatePickerFieldTrigger />}
        onChange={(date) => onChange(date ? format(date, current.hour, current.minute, current.meridiem) : "")}
      />
      <Select id={`${id}-hour`} label="Hour" value={current.hour} options={HOUR_OPTIONS} disabled={disabled || !current.date} onChange={(hour) => updateTime(hour, current.minute, current.meridiem)} />
      <Select id={`${id}-minute`} label="Minute" value={current.minute} options={MINUTE_OPTIONS} disabled={disabled || !current.date} onChange={(minute) => updateTime(current.hour, minute, current.meridiem)} />
      <Select id={`${id}-meridiem`} label="AM / PM" value={current.meridiem} options={MERIDIEM_OPTIONS} disabled={disabled || !current.date} onChange={(meridiem) => updateTime(current.hour, current.minute, meridiem as Meridiem)} />
    </div>
  </fieldset>;
}
